/**
 * SlxControls 1.2.41 ({@link https://www.selerix.com})
 * This is unpublished proprietary source code of Selerix Systems, Inc. The
 * copyright notice above is not evidence any actual or intended
 * publication of such source code.
 * @copyright Copyright 2025 Selerix Systems, Inc. All Rights Reserved.
 */
/**
 * SlxNumericInput class.
 * @requires slx.core
 * @class slx.numericInput
 */
var SlxNumericInput = (function (global) {
	'use strict';
	var x = global.SlxControl;

	// data types
	var SlxNumberType = {
		integer: 0,
		decimal: 1,
		money: 2,
		percent: 3
	};

	/**
	 * slx.numericInput
	 * @constructs
	 */
	x.slx.numericInput = function () {
		var that = this;
		x.slx.base.apply(that, arguments);

		// reference current element
		var el = that._element;
		if (el === void 0) { return this; }
		else if (el.length === 0) { return el; }

		// set custom settings from data props and attributes
		that.id = that.id || el.slxUniqueId();
		that.selector = '#' + that.id;
		that.onChange = typeof that.onChange === 'function' ? that.onChange : el.attr('onchange') !== void 0 ? x.eval.call(el[0], el.attr('onchange'), 'event') : void 0;
		that.required = el.prop('required');
		that.readonly = el.prop('readonly');
		that.disabled = el.prop('disabled');
		that.removeDelimeters = el.data('remove-delimeters') || false;
		that.decimalPlaces = that.decimalPlaces || el.data('decimal-places') || 0;
		that.min = that.min || (el.data('min') === void 0 ? null : el.data('min'));
		that.max = that.max || (el.data('max') === void 0 ? null : el.data('max'));
		that.step = that.step || (el.data('step') === void 0 ? 1 : el.data('step'));
		that.step = typeof that.step === 'number' ? that.step : Number.parseFloat(that.step);
		if (Number.isNaN(that.step)) {
			that.step = 1;
		}
		that.defaultValue = that.defaultValue || (el.data('default-value') === void 0 ? null : el.data('default-value'));
		that.emptyIf = that.emptyIf || (el.data('empty-if') === void 0 ? null : el.data('empty-if'));
		that.prefix = that.prefix || (el.data('prefix') === void 0 ? null : el.data('prefix'));
		that.suffix = that.suffix || (el.data('suffix') === void 0 ? null : el.data('suffix'));

		// set default value when not allowing empty values
		if (that.emptyIf === 'never' && !that.defaultValue) {
			that.defaultValue = that.min > 0 ? that.min : 0;
		}

		var t = that.type || el.data('type') || SlxNumberType.integer;
		that.type = typeof t === 'string' ? SlxNumberType[t] || SlxNumberType.integer : t
		if (that.type === SlxNumberType.integer && that.decimalPlaces > 0) {
			that.type = SlxNumberType.decimal;
		} else if (that.type === SlxNumberType.money && el.data('decimal-places') === void 0) {
			that.decimalPlaces = 2;
		}

		// determine prefix or suffix by number type
		if (that.prefix === null && that.suffix === null) {
			switch (that.type) {
				case SlxNumberType.money:
					that.prefix = '$';
					break;
				case SlxNumberType.percent:
					that.suffix = '%';
					break;
				default:
					break;
			}
		}

		// convert to number
		var _convert = function(value) {
			var v = value;

			// return if already a number
			if (typeof v === 'number') {}
			// convert to number
			else if (typeof v === 'string') {
				v = v.replace(/[^\d.\-(]/g, '').replace(/[(]/g, '-');
				v = v.indexOf('.') >= 0 ? Number.parseFloat(v) : Number.parseInt(v, 10);
			} else {
				v = NaN;
			}
			
			return Number.isNaN(v) ? null : v;
		};

		// format value
		var _formatValue = function(value, withCommas) {
			var commas = withCommas !== void 0 ? withCommas : true;
			var v = _convert(value);

			// validate max range
			if (that.max !== null && v > that.max) {
				v = that.max;
			}
			// validate min range
			else if (that.min !== null && v < that.min) {
				v = that.min;
			}

			// money
			var options = null;
			var isCurrency = false;
			switch (that.type) {
				// format as integer
				case SlxNumberType.integer:
					options = { maximumFractionDigits: 0 };
					break;
				// format as decimal
				case SlxNumberType.decimal:
					options = { style: 'decimal', maximumFractionDigits: that.decimalPlaces };
					break;
				// format as currency
				case SlxNumberType.money:
					options = { style: 'currency', currency: 'USD', maximumFractionDigits: that.decimalPlaces };
					isCurrency = true;
					break;
				// format as percentage
				case SlxNumberType.percent:
					v /= 100;
					options = { style: 'percent', maximumFractionDigits: that.decimalPlaces };
					break;
				// default format
				default:
					options = { maximumFractionDigits: that.decimalPlaces };
					break;
			}
			
			// use localized number format
			var f;
			try {
				f = new Intl.NumberFormat('en-US', options).format(v);
			} catch (ex) {
				if (isCurrency && that.decimalPlaces < 2) {
					options.maximumFractionDigits = 2;
					f = new Intl.NumberFormat('en-US', options).format(v);
				} else {
					throw ex;
				}
			}
			return commas ? f : f.replace(/[,]/g, '');
		};

		/**
		 * Set disabled property
		 * @param {boolean} v - true to disable the control
		 */
		that.setDisabled = function (v) {
			that.disabled = v === true;
			el.prop('disabled', that.disabled);
		};
		/**
		 * Set readonly property
		 * @param {booelean} v - true to set the control to readonly
		 */		
		that.setReadonly = function (v) {
			that.readonly = v === true;
			el.prop('readonly', that.readonly);
		};
		/**
		 * Set required property
		 * @param {boolean} v - true to set the control to required
		 */
		that.setRequired = function (v) {
			that.required = v === true;
			el.prop('required', that.required);
		};
		/**
		 * Get or set the value of the element
		 * @param {string} [v] - Optional value to set
		 * @returns {string} The current value of the element
		 */
		that.value = function (v) {
			if (v === void 0) {
				// get value without delimeters
				if (that.removeDelimeters) {
					return el.slxVal().replace(/[^\d.]/g, '');
				}
				// get value
				return el.slxVal();
			} else {
				// set value
				var newval = that.emptyIf === 'never' && (v === '' || v === null) ? that.defaultValue : v;
				var ischange = newval !== el.slxVal();
				var h = el.slxVal(_formatValue(newval));
				if (ischange) { el.trigger('change'); }
				return h;
			}
		};
		/**
		 * Get or set the text of the element
		 * @param {string} [v] - Optional text to set
		 * @returns {string} The current text of the element
		 */
		that.text = function (v) {
			if (v === void 0) {
				// get text
				return el.slxVal();
			} else {
				// set text
				if (v === '') { that.value(''); }
				return el.slxVal(v);
			}
		};
		/**
		 * Get or set the minimum value of the element
		 * @param {number} v - The minimum value to set
		 */
		that.setMin = function (v) {
			that.min = typeof v === 'number' ? v : that.min;
		};
		/**
		 * Get or set the maximum value of the element
		 * @param {number} v - The maximum value to set
		 */
		that.setMax = function (v) {
			that.max = typeof v === 'number' ? v : that.max;
		};
		/**
		 * Set the range of the element
		 * @param {number} min - The minimum value
		 * @param {number} max - The maximum value
		 */
		that.setRange = function (min, max) {
			that.setMin(min);
			that.setMax(max);
		};
		/**
		 * Set the stop increment of the element
		 * @param {number} v - The step incrrement to set
		 */
		that.setStep = function (v) {
			that.step = typeof v === 'number' ? v : that.step;
		};

		/**
		 * Get the number value of the element
		 * @returns {number} The number value of the element
		 * @returns {null} If the value is not a number
		 */
		that.numberValue = function() {
			return _convert(that.value());
		};

		/* initialization */
		(function () {
			el.prop('type', 'text');

			// empty out onchange attribute since this handler was already extracted and locally stored
			el.attr({ onchange: null, autocomplete: 'none' });

			// set props from settings
			el.prop({
				required: that.required,
				readonly: that.readonly,
				disabled: that.disabled
			});

			// add css class if needed and original val
			if (!el.hasClass('slx-numericinput')) { el.addClass('slx-numericinput'); }
			if (!el.data('original-val')) el.data('original-val', el.slxVal());

			// fill initial value with mask
			var v = el.slxVal();
			if (v === '' && (that.defaultValue || that.defaultValue === 0)) {
				v = that.defaultValue;
			}

			// format value
			var n = _convert(v);
			if (n !== null && n !== that.emptyIf) {
				v = _formatValue(n);
			}

			// html encode value and trigger render event
			el.slxVal(x.htmlEncode(v));
			setTimeout(function () { el.trigger('render'); });
		})();

		/* events */
		(function () {
			// element change event
			el.off('change.slx.numericinput').on('change.slx.numericinput', function (event) {
				if (typeof that.onChange === 'function') {
					that.onChange.call(el[0], event);
				}
			});
			// element on focus event
			el.off('focusin.slx.numericinput').on('focusin.slx.numericinput', function (event) {
				// set initial value and set cursor
				if (!el.slxVal()) {
					el.slxVal(_formatValue(0));
				} else {
					el.slxVal(_formatValue(_convert(el.slxVal()), false));
				}

				// move cursor to first numeric character
				var p;
				if (that.prefix) {
					p = that.prefix.toString().length;
					el.setSelectionRange(p, el.slxVal().length);
				} else if (that.suffix) {
					p = el.slxVal().indexOf(that.suffix.toString());
					el.setSelectionRange(0, p);
				} else {
					el.setSelectionRange(0, el.slxVal().length);
				}
			});
			// element on leaving focus event
			el.off('focusout.slx.numericinput').on('focusout.slx.numericinput', function (event) {
				// convert value to number
				var v = _convert(el.val());
				
				// set to default if never empty
				if (v === null && that.emptyIf === 'never') {
					that.value(that.defaultValue);
				}
				// clear value if treated as empty
				else if (v === that.emptyIf || (v === null && that.emptyIf !== 'never')) {
					el.slxVal('');
				}
				// format value on unfocus
				else {
					that.value(_formatValue(v));
				}

				// trigger change event
				if (el.data('original-val') !== that.value()) {
					el.data('original-val', that.value());
					el.trigger('change');
				}
			});
			// element key press event
			el.off('keydown.slx.numericinput').on('keydown.slx.numericinput', function (event) {
				// determine key pressed
				var k = event.keyCode || event.which, char = String.fromCharCode(k);
				var v = that.numberValue() || 0, val, enter = k === 13;

				// set cursor
				var cursor = el.getSelectionRange(), p = 0;

				// handle incrementation and decrementation
				if (k === 38 || k === 40) {
					event.preventDefault();

					// up arrow incrementation
					if (k === 38) {
						v = that.max !== null && v + that.step > that.max ? that.max : v + that.step;
					}
					// down arrow decrementation
					else if (k === 40) {
						v = that.min !== null && v - that.step < that.min ? that.min : v - that.step;
					}

					// set updated value
					el.slxVal(_formatValue(v).replace(/[,]/g, ''));

					// move cursor to first numeric character
					if (that.prefix) {
						p = that.prefix.toString().length;
					}
					// move cursor to last numeric character
					else if (that.suffix) {
						p = that.value().indexOf(that.suffix);
					}
					// keep same cursor position
					else if (cursor !== null) {
						p = cursor.start;
					}

					el.setSelectionRange(p, p);
				}

				// ignore certain keys
				var ignore = [8, 9, 16, 17, 35, 36, 37, 38, 39, 40, 46];
				if (ignore.contains(k)) {
					return;
				}

				// validate character
				if (k >= 96 && k <= 105) { k -= 48; char = String.fromCharCode(k); }
				
				// dot/decimal character
				var dot = k === 110 || (k === 190 && !event.shiftKey);

				// check for dot already existing and move cursor
				var dotIndex = el.slxVal().indexOf('.');
				if (dot && dotIndex > -1) {
					event.preventDefault();
					p = el.slxVal().indexOf('.') + 1;
					el.setSelectionRange(p, p);
					return
				}
				// enter new dot if not already existing
				else if (dot && that.decimalPlaces > 0) {
					return;
				}

				// validate numeric entry
				var minus = k === 109 || (!event.shiftKey && k === 189);
				if (!minus && !/\d/g.test(char) || event.shiftKey) {
					event.preventDefault();
					return;
				}

				// insert numeric character
				if (cursor !== null) {
					p = cursor.start;
					var end = cursor.end;
					if ((minus && p !== 0) || (minus && el.slxVal().indexOf('-') > -1)) {
						event.preventDefault();
						return;
					}
					val = el.slxVal();

					// fix cursor position
					var prefixIndex = val.indexOf(that.prefix);
					var suffixIndex = val.indexOf(that.suffix);
					// fix cursor at prefix
					if (!minus && that.prefix && prefixIndex > -1 && p <= prefixIndex) {
						p = prefixIndex + that.prefix.length;
						end = end < p ? p : end;
						el.setSelectionRange(p, end);
					}
					// fix cursor at suffix
					else if (that.suffix && suffixIndex > -1 && p >= suffixIndex) {
						if (p > suffixIndex || (p === suffixIndex && that.decimalPlaces > 0 && val.indexOf('.') > -1 && val.substring(0, suffixIndex).split('.')[1].length === that.decimalPlaces)) {
							event.preventDefault();
							return;
						}
						
						p = suffixIndex;
						end = end < p ? p : end;
						el.setSelectionRange(p, end);
					}

					if (that.decimalPlaces > 0) {
						if (dotIndex > -1 && p > dotIndex) {
							// don't allow decimal places beyond the maximum
							if (p === val.length && val.split('.')[1].length === that.decimalPlaces) {
								event.preventDefault();
								return;
							} else {
								event.preventDefault();
								val = el.slxVal().splice(p, 1, char);
								el.slxVal(val);
								p++;
								el.setSelectionRange(p, p);
							}
						}
					}
				}
			});
		})();

		// register control w/ element
		el[0].slxNumericInput = this;
		el[0].isSlxControl = true;

		return el;
	};
	// update constructor
	x.slx.numericInput.prototype = Object.create(x.slx.base.prototype);
	x.slx.numericInput.prototype.constructor = x.slx.numericInput;

	/* extenders */
	x.slx.extend({
		// add control init extension
		slxNumericInput: function () {
			var slx = this.slxControl('slxNumericInput', arguments) || new x.slx.numericInput(this, arguments);
			if (arguments.length > 0) {
				var arg = arguments[0], args = Array.prototype.slice.call(arguments, 1);
				if (typeof arg === 'string' && slx.hasOwnProperty(arg)) { return typeof (slx[arg]) === 'function' ? slx[arg].apply(slx, args) : slx[arg]; }
				else if (typeof arg !== 'string') { x.extend(slx, arg); }
			}
			return this;
		}
	});
	
	// register control
	x.slx.register('slxNumericInput', x.slx.slxNumericInput);

	// initialize all controls by CSS class
	x(function () { x('.slx-numericinput').each(function () { x(this).slxNumericInput(); }); });

	return x;
})(window || this);